import React, { useState, useEffect } from "react";
import { useAuth } from "../../components/AuthContext/AuthContext";
import ScreeningSection from "../../stories/Forms/ScreeningSection";
import ExitConsultationButton from "../../stories/Buttons/ExitConsultationButton";
import ExitModal from "../../stories/Modals/ExitModal";
import {
  getScreeningQuestions,
  getAdvicePrompts,
  getPatientConsent,
  SCREENING_FIELD_NAMES,
  getDisableTriggerFields,
  vitalityDefaultScreeningQuestions,
  links,
} from "../../stories/Forms/fields";
import PageLayout from "../../components/Layout/PageLayout";
import { Button } from "../../stories/Buttons/Button";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import img from "../../assets/images/notification.svg";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import Notes from "../../stories/Forms/Notes";
import { useConsultationDraft } from "../../hooks/useConsultationDraft";
import { useLocation, useNavigate } from "react-router";
import Spinner from "react-bootstrap/Spinner";
import { patientConsentUrl } from "../../utils/constants";
import useApis from "../../services/useAPI";

const ALLOWED_STAGES = [
  "patient-details",
  "clinical-screening",
  "clinical-measurement",
];

const ClinicalScreening = () => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const bookingId = location.state?.bookingId;
  const serviceName = location.state?.serviceName;
  const patientSex = location.state?.patientSex;
  const serviceType = location.state?.serviceType;
  const displayUserInfo =
    location.state?.displayUserInfo ?? auth.displayUserInfo;

  const isEditMode = location?.state?.isEditMode ?? false;

  const { draft, isLoaded, saveSlice, finalizeDraft } = useConsultationDraft(
    bookingId,
    serviceName,
    patientSex,
    serviceType,
  );
  const { callApi, isLoading: isContinuing } = useApis();

  const screeningQuestions = getScreeningQuestions(serviceName);
  const resolvedAdvicePrompts =  getAdvicePrompts(serviceName, serviceType);
  const [triggerQ1, triggerQ2] = getDisableTriggerFields(serviceName);

  const [questionValues, setQuestionValues] = useState({});
  const [adviceValues, setAdviceValues] = useState({});
  const [consentValues, setConsentValues] = useState({});
  const [notes, setNotes] = useState("");
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);

  useEffect(() => {
    if (
      !ALLOWED_STAGES.includes(sessionStorage.getItem("allowedStage")) &&
      auth?.featureFlag
    ) {
      navigate("/patient-record");
    }
  }, [auth?.featureFlag, navigate]);

  useEffect(() => {
    if (isLoaded) {
      if (draft?.screening_questions) {
        setQuestionValues(draft.screening_questions.answers || {});
        setAdviceValues(draft.screening_questions.advice_prompts || {});
        setConsentValues(draft.screening_questions.patient_consent || {});
      }
      setNotes(draft?.notes || "");
    }
  }, [isLoaded]);

  const isMale = patientSex === "male";

  const forcedDisabledQuestionNames = isMale
    ? [SCREENING_FIELD_NAMES.PREGNANT_OR_GIVEN_BIRTH]
    : [];

  const q1Value = questionValues[triggerQ1];
  const q2Value = questionValues[triggerQ2];
  const shouldDisableRest = q1Value === true || q2Value === true;
  const activeQuestionNames = (() => {
    if (q1Value === true) return [triggerQ1];
    if (q2Value === true) {
      return q1Value !== undefined ? [triggerQ1, triggerQ2] : [triggerQ2];
    }
    return [triggerQ1, triggerQ2];
  })();

  useEffect(() => {
    if (shouldDisableRest) {
      setQuestionValues((prev) => {
        const cleared = { ...prev };
        screeningQuestions.forEach((q) => {
          if (!activeQuestionNames.includes(q.name)) {
            delete cleared[q.name];
          }
        });
        return cleared;
      });
      setAdviceValues({});
      setConsentValues({});
      setNotes("");
    }
  }, [shouldDisableRest, activeQuestionNames.join(",")]);

  useEffect(() => {
    if (!isLoaded) return;

    const allConsentGiven = getPatientConsent(serviceName)
      .filter((item) => item.required !== false)
      .every((item) => !!consentValues[item.name]);

    saveSlice({
      screening_questions: {
        answers: questionValues,
        advice_prompts: adviceValues,
        patient_consent: consentValues,
      },
      service_name: serviceName,
      notes,
      consented: allAdviceChecked && allConsentGiven,
    });
  }, [questionValues, adviceValues, consentValues, notes, isLoaded]);

  const handleQuestionChange = (name, value) => {
    setQuestionValues((prev) => ({ ...prev, [name]: value }));

    if (value === true && (name === triggerQ1 || name === triggerQ2)) {
      setIsExitModalOpen(true);
    }
  };

  const handleExitConsultation = () => setIsExitModalOpen(true);
  const handleCloseExitModal = () => setIsExitModalOpen(false);
  const handleConfirmExit = (result) => {
  setIsExitModalOpen(false);
  if (result?.success) {
    navigate("/consultation-cancelled", {
      state: {
        rejectionMessage: `Consultation rejected: ${result.rejection_reason}`,
      },
    });
  }
};

  const allQuestionsAnswered = [
    ...vitalityDefaultScreeningQuestions,
    ...screeningQuestions,
  ].every(
    (q) =>
      forcedDisabledQuestionNames.includes(q.name) ||
      questionValues[q.name] !== undefined,
  );

  const allAdviceChecked = resolvedAdvicePrompts.every(
    (item) => !!adviceValues[item.name],
  );

  const allConsentChecked = getPatientConsent(serviceName)
    .filter((item) => item.required !== false)
    .every((item) => !!consentValues[item.name]);

  const isFormComplete =
    allQuestionsAnswered && allAdviceChecked && allConsentChecked;

   const handleContinue = async () => {
    const result = await callApi(
      { booking_id: bookingId, stage: "clinical-screening", is_edit: isEditMode },
      patientConsentUrl,
      "POST"
    );

    if (result?.success) {
      auth?.allowStage("clinical-screening");
      navigate("/clinical-measurement", {
        state: {
          bookingId,
          serviceType,
          serviceName,
          patientSex,
          displayUserInfo,
          isEditMode
      },
      });
    }
  };

  if (!isLoaded) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" className="my-custom-spinner" />
      </div>
    );
  }

  return (
    <LoggedInLayout
      buttonLabel="Logout"
      img={img}
      isActivation={false}
      links={links}
      logo={logo}
      pageTitle={auth.title}
      userInitials={auth.userInitials}
      vitalityLayout={auth?.featureFlag}
      isVhlogin={auth?.featureFlag}
    >
      <ExitConsultationButton onClick={handleExitConsultation} />

      <ExitModal
        isOpen={isExitModalOpen}
        onClose={handleCloseExitModal}
        onConfirm={handleConfirmExit}
        bookingId={bookingId}
        notes={notes}
        onNotesChange={setNotes}
      />

      <PageLayout
        className="clinical-screening-page"
        heading="Eligibility & Consent"
        subHeading={serviceType === "private-service" ? "   " : "Pre-check questionnaire and patient acknowledgements before measurements."}
        infoText="All eligibility & clinical screening questions must be completed, advice prompts checked and mandatory consent confirmed before proceeding."
      >
        <div className="clinical-screening-content">
          <ScreeningSection
            title="Eligibility"
            type="radio"
            items={vitalityDefaultScreeningQuestions}
            values={questionValues}
            onChange={handleQuestionChange}
            disabled={shouldDisableRest}
            activeNames={activeQuestionNames}
            forcedDisabledNames={forcedDisabledQuestionNames}
            className="screening-questions-card"
          />

          <div className="screening-divider"></div>
          <ScreeningSection
            title="Clinical screening"
            type="radio"
            items={screeningQuestions}
            values={questionValues}
            onChange={handleQuestionChange}
            disabled={shouldDisableRest}
            className="screening-questions-card"
          />

          <div className="screening-divider"></div>

          <ScreeningSection
            title="Advice prompts"
            type="checkbox"
            items={resolvedAdvicePrompts}
            values={adviceValues}
            onChange={(name, checked) =>
              setAdviceValues((prev) => ({ ...prev, [name]: checked }))
            }
            disabled={shouldDisableRest}
            className="screening-advice-card"
          />

          <div className="screening-divider"></div>

          <ScreeningSection
            title="Consent"
            type="checkbox"
            items={getPatientConsent(serviceName)}
            values={consentValues}
            onChange={(name, checked) =>
              setConsentValues((prev) => ({ ...prev, [name]: checked }))
            }
            disabled={shouldDisableRest}
            className="screening-consent-card"
          />

          <div className="screening-divider"></div>

          <Notes
            value={notes}
            onChange={setNotes}
            disabled={shouldDisableRest}
          />

          <div className="button-layout">
            <Button
              className="m-2 ml-0"
              label="Continue"
              onClick={handleContinue}
              size="small"
              type="button"
              variant="primary"
              isDisabled={!isFormComplete}
            />
          </div>
        </div>
      </PageLayout>
    </LoggedInLayout>
  );
};

export default ClinicalScreening;
